import React from 'react';
import { JobDetailsListItem } from '../../jobs/JobDetailsListItem';
import { Box, HStack, Icon, Text } from '@medely/ui-kit';
import { IAssignment } from '@medely/types';
import { useAssignmentRequestsForAssignmentGeneralInfo } from './graphql';
import { Link } from '@mp/components/Link';
import { cityZip, couldShowFlexibleDates, fullAddress } from '../utils';
import { COVID_REQUIREMENTS, DisplayHelpers } from '@mp';
import { HelpCenterLink } from '@mp/components/helpCenterLink';

type AssignmentGeneralInfoProps = {
  assignment: IAssignment;
  smallIcon?: boolean;
};

export const AssignmentGeneralInfo = ({
  smallIcon = false,
  assignment,
}: AssignmentGeneralInfoProps) => {
  const { assignmentRequests } = useAssignmentRequestsForAssignmentGeneralInfo({
    assignment_id: assignment.id,
  });

  const isAppliedOfferedAccepted = assignmentRequests?.find((req) =>
    ['applied', 'offer_sent', 'accepted'].includes(req.status ?? ''),
  );
  const isInProgress = ['in_progress'].includes(assignment.status);
  const showLocationLink = isAppliedOfferedAccepted || isInProgress;

  const covidRequirement =
    assignment.primary_location.covid_requirement ||
    assignment.primary_location.state?.covid_requirement;

  const showsStipends = assignment.is_w2 && assignment.is_travel;

  return (
    <>
      <Box pt={2}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-location-dot"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          title="Location"
        >
          {showLocationLink ? (
            <Link
              url={`https://maps.google.com/?q=${fullAddress(assignment)} ${cityZip(assignment)}`}
              variant="text"
              size="m"
            >
              <HStack flexWrap="wrap">
                <Text size="m" color="state.teal.primary">
                  {assignment.primary_location?.name}
                </Text>
                <Text size="m" color="state.teal.primary">
                  {assignment.primary_location?.address_first}
                </Text>
                <Text size="m" color="state.teal.primary">
                  {DisplayHelpers.location.cityStateZip(assignment.primary_location)}
                </Text>
              </HStack>
            </Link>
          ) : (
            <Text size="m" color="state.teal.primary">
              {DisplayHelpers.location.cityStateZip(assignment.primary_location)}
            </Text>
          )}
          <Text size="m">
            {`Facility Type: ${assignment.primary_location?.location_type?.label ?? ''}`}
          </Text>
        </JobDetailsListItem>
      </Box>
      <Box pt={2}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-calendar"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          title={
            couldShowFlexibleDates(assignment)
              ? 'Tentative start date / end date'
              : 'Start Date / end date'
          }
        >
          <Text size="m">{`${DisplayHelpers.assignment.assignmentStartDateAndEndDate(
            assignment.primary_location.timezone_lookup,
            assignment.starts_date,
            assignment.ends_date,
          )}`}</Text>
        </JobDetailsListItem>
      </Box>
      <Box pt={2}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-clock"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          title="Shift"
        >
          <Text size="m">
            {DisplayHelpers.assignment.time(
              assignment.primary_location.timezone_lookup,
              assignment.starts_time,
              assignment.ends_time,
              assignment.number_of_shifts_per_week,
            )}
          </Text>
          {assignment.variable_shift_times && <Text size="m">Actual shift times may vary</Text>}
        </JobDetailsListItem>
      </Box>
      {assignment.specialties && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-star-of-life"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Specialties"
          >
            <Text size="m">{DisplayHelpers.assignment.specialties(assignment.specialties)}</Text>
          </JobDetailsListItem>
        </Box>
      )}
      {assignment.surgery_types && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="ligh-scalpel"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Procedures"
          >
            <Text size="m">{DisplayHelpers.assignment.procedures(assignment)}</Text>
          </JobDetailsListItem>
        </Box>
      )}
      {assignment.patient_population && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-head-side-medical"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Patient population"
          >
            <Text size="m">{DisplayHelpers.assignment.patientPopulation(assignment)}</Text>
          </JobDetailsListItem>
        </Box>
      )}
      {assignment.ehr_system && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-window"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="EHR system"
          >
            <Text size="m">{assignment?.ehr_system.label}</Text>
          </JobDetailsListItem>
        </Box>
      )}
      {covidRequirement && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-syringe"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Covid protocol"
          >
            <Text size="m">
              {COVID_REQUIREMENTS.info[covidRequirement as keyof typeof COVID_REQUIREMENTS.info]}
              <HelpCenterLink text=" Learn More." configKey="covidVaccineOrTest" />
            </Text>
          </JobDetailsListItem>
        </Box>
      )}
      {showsStipends && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-house"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Housing/M&amp;IE stipends"
          >
            <Text size="m">Non-taxable income for housing, meals & incidental expenses</Text>
          </JobDetailsListItem>
        </Box>
      )}
    </>
  );
};
